import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { Badge } from '../../../components/ui/badge';
import { IconCheck, IconX, IconClock, IconTrendingUp, IconAlertTriangle } from '@tabler/icons-react';
import { cn } from '../../../lib/utils';
import coverageVerificationAnalyticService from '../../../services/coverage-verification-analytics.service';

const SuccessRateCard = ({ organization, dateRange, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await coverageVerificationAnalyticService.getCoverageCheckSuccessRate({
          organization,
          dateRange,
        });
        setData(result);
      } catch (err) {
        console.error('Failed to fetch success rate:', err);
        setError('Failed to load success rate data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organization, dateRange, refreshKey]);

  if (error) {
    return (
      <Card className="shadow-sm border-0">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <IconAlertTriangle size={20} />
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  const getSuccessRateColor = (rate) => {
    if (rate >= 95) return 'success';
    if (rate >= 85) return 'warning';
    return 'danger';
  };

  const successRate = data?.success_rate || 0;
  const colorClass = getSuccessRateColor(successRate);

  return (
    <>
      <Card className="shadow-sm border-0 mb-6">
        <CardHeader>
          <CardTitle className="text-base font-bold">Coverage Check Success Metrics</CardTitle>
          <CardDescription className="text-sm">
            Overall performance and reliability of coverage verification checks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {/* Success Rate */}
            <Card className="shadow-sm border-0 bg-gradient-to-br from-green-50 to-emerald-50">
              <CardContent className="pt-6">
                <div className="flex justify-between items-center mb-3">
                  <div className={cn(
                    'p-2 rounded',
                    loading ? 'bg-gray-400 text-white' :
                    colorClass === 'success' ? 'bg-green-500 text-white' :
                    colorClass === 'warning' ? 'bg-yellow-500 text-white' :
                    'bg-red-500 text-white'
                  )} style={{ width: 40, height: 40 }}>
                    <IconTrendingUp size={24} />
                  </div>
                  {!loading && (
                    <Badge variant={
                      colorClass === 'success' ? 'default' : 
                      colorClass === 'warning' ? 'warning' : 
                      'destructive'
                    } className="text-xs">
                      {colorClass === 'success' ? 'Excellent' : 
                       colorClass === 'warning' ? 'Good' : 
                       'Needs Attention'}
                    </Badge>
                  )}
                </div>
                <div>
                  <h3 className="text-3xl font-bold mb-1">
                    {loading ? <Skeleton className="w-24 h-8" /> : `${successRate.toFixed(1)}%`}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-0">Success Rate</p>
                </div>
              </CardContent>
            </Card>

            {/* Total Checks */}
            <Card className="shadow-sm border-0 bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardContent className="pt-6">
                <div className="flex justify-between items-center mb-3">
                  <div className="p-2 rounded bg-blue-500 text-white" style={{ width: 40, height: 40 }}>
                    <IconCheck size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-3xl font-bold mb-1">
                    {loading ? <Skeleton className="w-20 h-8" /> : data?.total_checks?.toLocaleString() || '0'}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-0">Total Checks</p>
                  {!loading && data?.completed_count > 0 && (
                    <p className="text-xs text-blue-600 mt-1">
                      {data.completed_count} completed
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Failed Checks */}
            <Card className="shadow-sm border-0 bg-gradient-to-br from-red-50 to-pink-50">
              <CardContent className="pt-6">
                <div className="flex justify-between items-center mb-3">
                  <div className="p-2 rounded bg-red-500 text-white" style={{ width: 40, height: 40 }}>
                    <IconX size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-3xl font-bold mb-1 text-red-600">
                    {loading ? <Skeleton className="w-16 h-8" /> : data?.failed_count || '0'}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-0">Failed Checks</p>
                  {!loading && data?.failed_count > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {((data.failed_count / data.total_checks) * 100).toFixed(1)}% failure rate
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Avg Response Time */}
            <Card className="shadow-sm border-0 bg-gradient-to-br from-purple-50 to-violet-50">
              <CardContent className="pt-6">
                <div className="flex justify-between items-center mb-3">
                  <div className="p-2 rounded bg-purple-500 text-white" style={{ width: 40, height: 40 }}>
                    <IconClock size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-3xl font-bold mb-1">
                    {loading ? <Skeleton className="w-24 h-8" /> : `${((data?.avg_response_time_ms || 0) / 1000).toFixed(2)}s`}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-0">Avg Response Time</p>
                  <p className="text-xs text-muted-foreground mt-1">Per API call</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Error Breakdown Table */}
      {!loading && data?.error_breakdown && data.error_breakdown.length > 0 && (
        <Card className="shadow-sm border-0 mb-6">
          <CardHeader>
            <CardTitle className="text-base font-bold">Error Breakdown</CardTitle>
            <CardDescription className="text-sm">
              Most common error types encountered
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Error Type</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Count</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {data.error_breakdown.map((error, index) => (
                    <tr key={index} className="border-b hover:bg-muted/50 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-medium">{error.error_category}</span>
                      </td>
                      <td className="py-3 px-4 text-right">{error.error_count}</td>
                      <td className="py-3 px-4 text-right">
                        <Badge variant="outline">
                          {error.percentage.toFixed(1)}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default SuccessRateCard;
